import { Extension } from '@tiptap/core';
import Suggestion, { SuggestionOptions } from '@tiptap/suggestion';
import type { Editor, Range } from '@tiptap/core';
// Loads starter-kit's module augmentations for ChainedCommands (toggleBulletList,
// toggleOrderedList, toggleBlockquote, setHorizontalRule). No runtime effect; consumers
// (e.g. AAB's RichAssetEditor) are responsible for actually registering StarterKit on
// their editor instance.
import '@tiptap/starter-kit';

export interface SlashMenuItem {
  id: string;
  title: string;
  hint: string;
  command: (props: { editor: Editor; range: Range }) => void;
}

export const SLASH_MENU_ITEMS: SlashMenuItem[] = [
  {
    id: 'h1',
    title: 'Heading 1',
    hint: 'Top-level section',
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setNode('heading', { level: 1 }).run();
    },
  },
  {
    id: 'h2',
    title: 'Heading 2',
    hint: 'Subsection',
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setNode('heading', { level: 2 }).run();
    },
  },
  {
    id: 'bullet',
    title: 'Bulleted list',
    hint: 'Quick points',
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBulletList().run();
    },
  },
  {
    id: 'ordered',
    title: 'Numbered list',
    hint: 'Ordered steps',
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleOrderedList().run();
    },
  },
  {
    id: 'quote',
    title: 'Quote',
    hint: 'Pull-out quote',
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBlockquote().run();
    },
  },
  {
    id: 'divider',
    title: 'Divider',
    hint: 'Horizontal break',
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setHorizontalRule().run();
    },
  },
];

export interface SlashMenuRendererPort {
  onUpdate: (state: {
    items: SlashMenuItem[];
    onSelect: (item: SlashMenuItem) => void;
    clientRect: (() => DOMRect | null) | null;
  } | null) => void;
}

export interface SlashMenuOptions {
  renderer: SlashMenuRendererPort;
}

export const SlashMenu = Extension.create<SlashMenuOptions>({
  name: 'aabSlashMenu',
  addOptions() {
    return { renderer: { onUpdate: () => undefined } };
  },
  addProseMirrorPlugins() {
    const renderer = this.options.renderer;
    const config: Omit<SuggestionOptions<SlashMenuItem, SlashMenuItem>, 'editor'> = {
      char: '/',
      startOfLine: true,
      items: ({ query }) =>
        SLASH_MENU_ITEMS.filter((i) => i.title.toLowerCase().includes(query.toLowerCase())),
      command: ({ editor, range, props }) => {
        props.command({ editor, range });
      },
      render: () => {
        return {
          onStart: (props) => {
            renderer.onUpdate({
              items: props.items,
              onSelect: (item) => props.command(item),
              clientRect: props.clientRect ?? null,
            });
          },
          onUpdate: (props) => {
            renderer.onUpdate({
              items: props.items,
              onSelect: (item) => props.command(item),
              clientRect: props.clientRect ?? null,
            });
          },
          onKeyDown: () => false,
          onExit: () => renderer.onUpdate(null),
        };
      },
    };
    return [Suggestion({ editor: this.editor, ...config })];
  },
});
